// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — AchievementToast
// ────────────────────────────────────────────────────────────────────
// The unlock notification. Grimoire-framed (GM palette, Cinzel, iron-gall
// gold, wax-seal glyph). Self-queuing: feed it an array of newly-granted
// ids (a grant can cascade meta achievements, so multiple can land at once)
// and it presents them one at a time, each auto-dismissing.
//
// ── USAGE ────────────────────────────────────────────────────────────
//   const [toastIds, setToastIds] = useState([]);
//   // after a grant:
//   const res = await grantAchievement(uid, "terror:beat_jynxi");
//   if (res.granted.length) setToastIds((q) => [...q, ...res.granted]);
//   ...
//   <AchievementToast ids={toastIds} onDrained={() => setToastIds([])} />
//
// It de-dupes against what it has already shown this mount, so re-feeding
// the same id (idempotent grants) won't double-toast.
// ════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef, useCallback } from "react";
import { GM } from "./theme";
import { ensureKeyframes } from "./PerryUI";
import {
  getAchievement,
  getAchievementRarity,
  getAchievementTint,
  getAchievementDisplay,
  GAME_NAMESPACES,
} from "./achievements";

const SHOW_MS = 4200; // time each toast stays on screen

export default function AchievementToast({ ids = [], onDrained }) {
  const [queue, setQueue] = useState([]);     // ids waiting to show
  const [current, setCurrent] = useState(null); // id currently shown
  const [leaving, setLeaving] = useState(false);
  const seenRef = useRef(new Set());           // de-dupe across the mount
  const timerRef = useRef(null);

  // Ingest new ids (de-duped) into the queue.
  useEffect(() => {
    if (!ids || ids.length === 0) return;
    const fresh = ids.filter((id) => !seenRef.current.has(id) && getAchievement(id));
    if (fresh.length === 0) return;
    fresh.forEach((id) => seenRef.current.add(id));
    setQueue((q) => [...q, ...fresh]);
  }, [ids]);

  // Pump the queue: when nothing is showing, pull the next id.
  useEffect(() => {
    if (current || queue.length === 0) return;
    const [next, ...rest] = queue;
    setQueue(rest);
    setCurrent(next);
    setLeaving(false);
  }, [queue, current]);

  const dismiss = useCallback(() => {
    setLeaving(true);
    // allow the exit animation to play, then clear
    window.setTimeout(() => {
      setCurrent(null);
      setLeaving(false);
    }, 360);
  }, []);

  // Auto-dismiss the current toast.
  useEffect(() => {
    if (!current) return;
    timerRef.current = window.setTimeout(dismiss, SHOW_MS);
    return () => window.clearTimeout(timerRef.current);
  }, [current, dismiss]);

  // When everything has drained, let the parent reset its feed.
  useEffect(() => {
    if (!current && queue.length === 0 && seenRef.current.size > 0 && onDrained) {
      // small debounce so a trailing grant in the same tick still queues
      const t = window.setTimeout(() => {
        if (!current && queue.length === 0) onDrained();
      }, 50);
      return () => window.clearTimeout(t);
    }
  }, [current, queue.length, onDrained]);

  if (!current) return null;

  const def = getAchievement(current);
  if (!def) return null;

  const rarity = getAchievementRarity(current);
  const tint = getAchievementTint(current);
  const gameLabel = GAME_NAMESPACES[def.game]?.label || "Illegal Empire";
  const display = getAchievementDisplay(current, true); // earned → reveal hidden

  // PERRY ADAPTATION: the harvested Terror version rendered a JSX
  // <style> block here. That is against the keyframe injection law —
  // a JSX <style> re-inserts on every render and can't self-heal a
  // stale rule from an earlier build. Same CSS, injected once,
  // id-guarded, through the one approved door.
  ensureKeyframes("perry-achievement-toast-keyframes", toastKeyframes);

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        onClick={dismiss}
        style={{
          position: "fixed",
          top: 18,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          minWidth: 300,
          maxWidth: "min(420px, 92vw)",
          cursor: "pointer",
          animation: leaving
            ? "ieAchOut 340ms cubic-bezier(.4,0,.6,1) forwards"
            : "ieAchIn 420ms cubic-bezier(.16,1,.3,1) forwards",
        }}
      >
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "14px 18px",
            background: `linear-gradient(180deg, ${GM.pageMid} 0%, ${GM.page} 100%)`,
            border: `1px solid ${GM.inkGold}`,
            borderRadius: 10,
            boxShadow: `0 0 0 1px ${GM.pageBorder}, 0 8px 28px rgba(0,0,0,0.55), 0 0 22px ${tint}44, inset 0 1px 0 ${GM.inkGold}33`,
            fontFamily: GM.font,
            overflow: "hidden",
          }}
        >
          {/* faint ruling-line texture */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background:
                "repeating-linear-gradient(0deg, transparent, transparent 13px, rgba(139,106,48,0.06) 13px, rgba(139,106,48,0.06) 14px)",
              pointerEvents: "none",
            }}
          />
          {/* rarity-tinted wax-seal medallion */}
          <div
            style={{
              flex: "0 0 auto",
              width: 52,
              height: 52,
              display: "grid",
              placeItems: "center",
              borderRadius: "50%",
              fontSize: 26,
              background: `radial-gradient(circle at 35% 30%, ${tint}cc, ${tint}55 60%, ${GM.page} 100%)`,
              border: `2px solid ${rarity.tint}`,
              boxShadow: `0 0 14px ${tint}66, inset 0 0 10px rgba(0,0,0,0.5)`,
              animation: "ieAchSeal 1600ms ease-in-out infinite",
            }}
          >
            <span style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))" }}>
              {display.icon}
            </span>
          </div>

          <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 9.5,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: rarity.tint,
                fontWeight: 700,
                marginBottom: 2,
              }}
            >
              {rarity.label} · Achievement Unlocked
            </div>
            <div
              style={{
                fontFamily: GM.fontD,
                fontSize: 17,
                lineHeight: 1.15,
                color: GM.vellum,
                fontWeight: 700,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {display.name}
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: GM.vellumDim,
                marginTop: 3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {display.desc}
            </div>
            <div
              style={{
                fontSize: 9,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: GM.vellumFaint,
                marginTop: 4,
              }}
            >
              {gameLabel}
              {def.points ? ` · ${def.points} renown` : ""}
            </div>
          </div>

          {/* diamond flourish */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: 6,
              right: 8,
              color: GM.inkGoldDim,
              fontSize: 9,
              opacity: 0.7,
            }}
          >
            ✦
          </div>
        </div>
      </div>
    </>
  );
}

const toastKeyframes = `
@keyframes ieAchIn {
  0%   { opacity: 0; transform: translateX(-50%) translateY(-22px) scale(0.96); }
  100% { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
}
@keyframes ieAchOut {
  0%   { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
  100% { opacity: 0; transform: translateX(-50%) translateY(-18px) scale(0.97); }
}
@keyframes ieAchSeal {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.06); }
}
`;
